import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Button, Space, Typography, Spin, Alert, Tag, Empty, Result } from 'antd';
import {
  CloseOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  WarningFilled,
  CloseCircleFilled,
  HeartOutlined,
} from '@ant-design/icons';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import {
  getAgentProvisioningHealth,
  type AgentProvisioningHealthReport,
  type HealthCheck,
  type HealthStatus,
  type HealthOverall,
} from '@/api/provisioningHealth';
import { trackEvent } from '@/lib/telemetry';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ProvisioningHealthDrawer — read-only operational diagnostic for one agent
// ─────────────────────────────────────────────────────────────────────────────
//
// PURPOSE
//   "Is this agent's full stack provisioned correctly?" — answers in seven
//   read-only checks (DB row, Foundry registration, skills, KB linkage, peer
//   wirings, test prompts, scenarios). Drives the "Provisioning Health"
//   button on AgentDetailPage.
//
// AUDIENCE
//   Operations engineers and clinical SMEs onboarding a new agent. Detail
//   strings are plain English (no C# class names, no SQL); each card carries
//   a remediation hint when status is not Healthy. The HelpPopover at the
//   top explains the diagnostic in nurse-friendly language.
//
// DESIGN
//   - 720px wide (room for the multi-line Detail blocks + remediation).
//   - Overall status banner at top — green/yellow/red.
//   - One card per area in the canonical order returned by the API.
//   - Recheck button (top-right) refetches without closing the drawer.
//   - No mutations — pure reads. Safe to leave open while iterating on
//     onboarding (wire peers → click Recheck → see green).
// ─────────────────────────────────────────────────────────────────────────────

interface ProvisioningHealthDrawerProps {
  open: boolean;
  agentId: number | null;
  onClose: () => void;
}

export const ProvisioningHealthDrawer = observer(function ProvisioningHealthDrawer({
  open,
  agentId,
  onClose,
}: ProvisioningHealthDrawerProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [report, setReport] = useState<AgentProvisioningHealthReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!agentId || agentId <= 0) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getAgentProvisioningHealth(agentId);
      setReport(r);
      trackEvent('Agent.ProvisioningHealth.Checked', {
        agentId,
        overall: r.overall,
        failingCount: r.checks.filter((c) => c.status === 'Failing').length,
        warningCount: r.checks.filter((c) => c.status === 'Warning').length,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && agentId) {
      void load();
    }
    if (!open) {
      // Clear stale state when the drawer closes so the next open starts
      // with a fresh fetch indicator instead of the previous agent's data.
      setReport(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      closeIcon={<CloseOutlined />}
      title={
        <Space size={8}>
          <HeartOutlined style={{ color: colors.ink[500] }} />
          <span>Provisioning health</span>
          <HelpHotspot topic="provisioning.health" variant="inline" label="What does this check?" />
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
          disabled={!agentId}
        >
          Recheck
        </Button>
      }
      bodyStyle={{ background: isDark ? colors.slate[900] : colors.slate[50] }}
    >
      {loading && !report && (
        <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin tip="Running 7 checks…" />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          message="Health check failed"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && !error && report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <OverallBanner report={report} isDark={isDark} />
          {report.checks.map((c) => (
            <CheckCard key={c.area} check={c} isDark={isDark} />
          ))}
          <Paragraph style={{ fontSize: 11, color: colors.slate[500], marginTop: 8, marginBottom: 0 }}>
            Checked {new Date(report.checkedAtUtc).toLocaleString()}.
            All checks are read-only — re-run as you iterate on onboarding.
          </Paragraph>
        </div>
      )}

      {!loading && !error && !report && agentId && (
        <Empty description="No data — click Recheck to run the diagnostic." />
      )}
    </Drawer>
  );
});

// ── Overall banner ───────────────────────────────────────────────────────────

function OverallBanner({
  report,
  isDark,
}: {
  report: AgentProvisioningHealthReport;
  isDark: boolean;
}) {
  const palette = overallPalette(report.overall);
  const failing = report.checks.filter((c) => c.status === 'Failing').length;
  const warnings = report.checks.filter((c) => c.status === 'Warning').length;

  return (
    <Result
      icon={paletteIcon(palette.tone)}
      status={palette.resultStatus}
      title={
        <span style={{ fontSize: 18 }}>
          {palette.title}
        </span>
      }
      subTitle={
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Text>
            <strong>{report.agentDisplayName ?? `Agent ${report.agentId}`}</strong>
            {report.canonicalName ? ` · ${report.canonicalName}` : null}
          </Text>
          <Text type="secondary">
            {failing > 0
              ? `${failing} blocker${failing === 1 ? '' : 's'}, ${warnings} warning${warnings === 1 ? '' : 's'}.`
              : warnings > 0
                ? `${warnings} warning${warnings === 1 ? '' : 's'} — functional but not yet production-grade.`
                : 'All seven checks passed.'}
          </Text>
        </Space>
      }
      style={{
        padding: '20px 16px',
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${palette.borderColor}`,
        borderRadius: 8,
      }}
    />
  );
}

function paletteIcon(tone: 'success' | 'warning' | 'error') {
  if (tone === 'success') return <CheckCircleFilled style={{ color: '#059669', fontSize: 36 }} />;
  if (tone === 'warning') return <WarningFilled style={{ color: '#D97706', fontSize: 36 }} />;
  return <CloseCircleFilled style={{ color: '#DC2626', fontSize: 36 }} />;
}

function overallPalette(overall: HealthOverall): {
  title: string;
  tone: 'success' | 'warning' | 'error';
  resultStatus: 'success' | 'warning' | 'error';
  borderColor: string;
} {
  if (overall === 'ReadyForProduction') {
    return {
      title: 'Ready for production',
      tone: 'success',
      resultStatus: 'success',
      borderColor: '#059669',
    };
  }
  if (overall === 'Issues') {
    return {
      title: 'Issues — review before declaring ready',
      tone: 'warning',
      resultStatus: 'warning',
      borderColor: '#D97706',
    };
  }
  return {
    title: 'Not ready — blockers to resolve',
    tone: 'error',
    resultStatus: 'error',
    borderColor: '#DC2626',
  };
}

// ── Per-area check card ──────────────────────────────────────────────────────

function CheckCard({ check, isDark }: { check: HealthCheck; isDark: boolean }) {
  const palette = statusPalette(check.status);
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${palette.borderColor}`,
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StatusChip status={check.status} />
        <Text strong style={{ fontSize: 15 }}>{check.displayName}</Text>
      </div>

      <Text style={{ fontSize: 13 }}>{check.summary}</Text>

      {check.detail && (
        <pre
          style={{
            margin: 0,
            padding: '8px 10px',
            background: isDark ? colors.slate[900] : colors.slate[50],
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 6,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            color: isDark ? colors.slate[200] : colors.slate[700],
          }}
        >
          {check.detail}
        </pre>
      )}

      {check.remediation && (
        <Alert
          type={check.status === 'Failing' ? 'error' : 'warning'}
          message={
            <span style={{ fontSize: 12 }}>
              <strong>Next step:</strong> {check.remediation}
            </span>
          }
          showIcon
        />
      )}
    </div>
  );
}

function StatusChip({ status }: { status: HealthStatus }) {
  const palette = statusPalette(status);
  return (
    <Tag
      color={palette.tagColor}
      style={{ fontWeight: 600, marginRight: 0, padding: '2px 10px' }}
    >
      {palette.label}
    </Tag>
  );
}

function statusPalette(status: HealthStatus): {
  label: string;
  tagColor: string;
  borderColor: string;
} {
  if (status === 'Healthy') {
    return { label: '✓ Healthy', tagColor: 'green', borderColor: '#059669' };
  }
  if (status === 'Warning') {
    return { label: '⚠ Warning', tagColor: 'orange', borderColor: '#D97706' };
  }
  return { label: '✗ Failing', tagColor: 'red', borderColor: '#DC2626' };
}
